import * as yup from 'yup';

import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  I_CAN_NOT_BOOK_A_TICKET,
  ONLINE_BOOKING,
  categoryList,
} from '@/constants/contact-us';
import {
  checkIfPhoneNumberIsValid,
  country,
  getDialCodeFromPhoneNumber,
  muviPhoneNumberTrimmer,
} from '@/logic/shared';
import { useAuth, useDialog, useSnack } from '@/logic/state';

import { BackwardButton } from '@/components/shared/backward-button';
import InputAdornmentComponent from '@/components/shared/Input-adornment-component';
import { LoadingButton } from '@mui/lab';
import ReCAPTCHA from 'react-google-recaptcha';
import dynamic from 'next/dynamic';
import { excludedCountriesList } from '@/constants/phone-numbers';
import getConfig from 'next/config';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useContactUs } from '@/logic/state/contact-us';
import { useCreateContactUs } from '@/logic/services/contact-us';
import { useForm } from 'react-hook-form';
import { useGetSettings } from '@/logic/services';
import { useRouter } from 'next/router';
import { yupResolver } from '@hookform/resolvers/yup';

const MuiPhoneNumber = dynamic(
  () => import('material-ui-phone-number').then(component => component.default),
  { ssr: false }
);

interface Form {
  phoneNumber: string;
  email: string;
  category: string;
  subject: string;
  description: string;
  customerName: string;
  recaptchaResponse: string;
}

const ReportMuviTeamDialog: FC = () => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();
  const setSnack = useSnack(s => s.setSnack, shallow);
  const [countryCode_contact, setCountryCode_contact] = useState('KSA');
  const [selectedCountry, setSelectedCountry] = useState<country>(null);

  const [dialog, setDialog] = useDialog(s => [s.dialog, s.setDialog], shallow);

  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { publicRuntimeConfig } = getConfig();
  const [recaptchaValue, setRecaptchaValue] = useState<string>('');
  const createContactUs = useCreateContactUs();
  const { reportMuviDialogPayload } = dialog;
  const contactUs = useContactUs(s => s.contactUs, shallow);
  const user = useAuth(s => s.user, shallow);

  const REQUIRED_VALIDATION_MESSAGE = intl.formatMessage({ id: 'global.required' });

  const schema = yup.object({
    phoneNumber: yup
      .string()
      .test(
        'test-valid-phone',
        intl.formatMessage({ id: 'global.phoneNumber.notValid' }),
        value => {
          if (value) {
            return checkIfPhoneNumberIsValid(value, countryCode_contact);
          }
        }
      ),
    customerName: yup.string().trim().required(), //because it includes first & last name

    email: yup
      .string()
      .required(REQUIRED_VALIDATION_MESSAGE)
      .email(intl.formatMessage({ id: 'global.validation.email' })),
    category: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    subject: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
    description: yup.string().required(REQUIRED_VALIDATION_MESSAGE),
  });

  const {
    register,
    setValue,
    handleSubmit,
    trigger,
    getValues,
    formState: { errors, touchedFields, isValid },
  } = useForm<Form>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      phoneNumber: dialog?.reportMuviDialogPayload?.data?.phoneNumber ?? user.phoneNumber,
      customerName: user?.firstName ? user?.firstName + ' ' + user?.lastName : '',
      email: dialog?.reportMuviDialogPayload?.data?.email ?? user.email,
      category: ONLINE_BOOKING,
      subject: dialog?.reportMuviDialogPayload?.data?.subject ?? '',
      description: contactUs?.reportDescriptionMessage,
    },
  });
  useEffect(() => {
    setSelectedCountry(prev => {
      if (dialog?.reportMuviDialogPayload?.data?.phoneNumber || user?.phoneNumber) {
        return {
          ...prev,
          dialCode: getDialCodeFromPhoneNumber(
            dialog?.reportMuviDialogPayload?.data?.phoneNumber ?? user?.phoneNumber
          ),
        };
      }
    });
  }, [dialog?.reportMuviDialogPayload?.data?.phoneNumber, user?.phoneNumber]);

  const onChangeCaptcha = useCallback(
    value => {
      setRecaptchaValue(value);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setValue]
  );

  const onSubmit = handleSubmit(
    async ({
      email,
      phoneNumber,
      category,
      subject,
      description,
      customerName,
      recaptchaResponse,
    }) => {
      const contactUsData: Form = {
        phoneNumber: muviPhoneNumberTrimmer(phoneNumber, selectedCountry.dialCode),
        email,
        category,
        subject,
        description,
        customerName,
        recaptchaResponse,
      };
      await createContactUs.safeMutateAsync(
        {
          data: {
            ...contactUsData,
            subcategory: I_CAN_NOT_BOOK_A_TICKET,
            recaptchaResponse: recaptchaValue || '',
          },
        },
        {
          onSuccess: () => {
            setRecaptchaValue('');
            setSnack({
              isOpen: true,
              message: 'report.muvi.dialog.success.message',
              type: 'success',
            });
            setDialog({});
          },
        }
      );
    }
  );
  const { data: allCmsSetting } = useGetSettings();

  return (
    <>
      <DialogTitle>
        <BackwardButton
          handleClick={() => reportMuviDialogPayload.handleBackwardClick()}
        />
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            margin: 'auto',
          }}
        >
          <Typography
            sx={{
              fontSize: {
                mobile: '22px',
                tablet: pxToRem(22),
              },
              color: theme.palette.grey[90],
              fontWeight: 'bold',
            }}
          >
            <FormattedMessage id='footer.link.contact.us' />
          </Typography>
          <Box>
            <Typography
              sx={{
                fontSize: {
                  mobile: '18px',
                  tablet: pxToRem(18),
                },
                color: theme.palette.primary.main,
                marginTop: '32px',
                fontWeight: 'bold',
              }}
            >
              <FormattedMessage id='contact_us.stay_connect' />
            </Typography>
            <Typography
              sx={{
                fontSize: {
                  mobile: '18px',
                  tablet: pxToRem(18),
                },
                color: theme.palette.grey[60],
                marginTop: '16px',
              }}
            >
              {allCmsSetting?.contactUsDescription}
            </Typography>
            <Stack spacing={3} sx={{ width: '100%', marginTop: '40px' }}>
              <FormControl>
                <TextField
                  id='contact-us-customerName'
                  label={<FormattedMessage id='global.full_name' />}
                  variant='outlined'
                  {...register('customerName')}
                  error={(errors.customerName && touchedFields.customerName) ?? false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentComponent
                        errorCondition={errors.customerName && touchedFields.customerName}
                      />
                    ),
                  }}
                  sx={{
                    '& fieldset span': {
                      opacity: user?.firstName ? '1!important' : '0',
                      transform: user?.firstName ? 'translateY(-5px)' : 'translateY(0)',
                    },
                    '&  label': {
                      opacity: !user?.firstName ? '1!important' : '0',
                    },
                  }}
                />
                <FormHelperText error>
                  {touchedFields.customerName &&
                    (errors?.customerName?.type === 'required' ? (
                      <FormattedMessage id='global.required' />
                    ) : errors?.customerName?.type === 'min' ||
                      errors?.customerName?.type === 'max' ? (
                      <>
                        <FormattedMessage id='global.please.enter.valid' />
                        &nbsp;
                        <FormattedMessage id='global.full_name' />
                        <FormattedMessage id='global.min.two.max.fifty' />
                      </>
                    ) : (
                      errors?.customerName?.type === 'matches' && (
                        <FormattedMessage id='global.only.chars.allowed' />
                      )
                    ))}
                </FormHelperText>
              </FormControl>
              <FormControl>
                <TextField
                  id='report-muvi-email'
                  {...register('email')}
                  error={Boolean(errors.email)}
                  helperText={errors.email?.message}
                  label={
                    <>
                      <FormattedMessage id='global.email' />
                      &nbsp;
                      <FormattedMessage id='global.required()' />
                    </>
                  }
                  variant='outlined'
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentComponent
                        errorCondition={errors.email && touchedFields.email}
                      />
                    ),
                  }}
                />
              </FormControl>
              <FormControl variant='standard'>
                <MuiPhoneNumber
                  excludeCountries={excludedCountriesList}
                  value={getValues().phoneNumber || ''}
                  error={
                    (errors.phoneNumber?.type === 'test-valid-phone' &&
                      touchedFields.phoneNumber) ??
                    false
                  }
                  helperText={errors.phoneNumber?.message}
                  id='phone'
                  InputLabelProps={{
                    sx: {
                      backgroundColor: 'white',
                      paddingInline: '6px',
                    },
                  }}
                  sx={{
                    '& svg': { height: '1em' },
                    legend: {
                      width: 0,
                    },
                  }}
                  defaultCountry={'sa'}
                  dir='ltr'
                  label={<FormattedMessage id='global.phone_number' />}
                  variant={'outlined'}
                  {...register('phoneNumber')}
                  onChange={(e, country?: country) => {
                    setSelectedCountry(country);
                    setValue('phoneNumber', e);
                    const field = 'phoneNumber';
                    const c = country?.countryCode.toString().toUpperCase();
                    if (field.includes('contact')) {
                      setCountryCode_contact(c);
                    }
                    trigger(); // only special handle for this mui phoneNumber field
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentComponent
                        errorCondition={
                          errors.phoneNumber?.type === 'test-valid-phone' &&
                          touchedFields.phoneNumber
                        }
                      />
                    ),
                  }}
                />
              </FormControl>
              <FormControl>
                <InputLabel
                  id='report-muvi-category-label'
                  error={(errors.category && touchedFields.category) ?? false}
                >
                  <FormattedMessage id='contact_us.category' />
                </InputLabel>
                <Select
                  disabled
                  labelId='category-label'
                  id='report-muvi-category-select'
                  label={<FormattedMessage id='contact_us.category' />}
                  value={getValues('category')}
                  {...register('category')}
                  error={Boolean(errors.category) && touchedFields.category}
                  onChange={(e: SelectChangeEvent) => {
                    setValue('category', e.target.value as string);
                    trigger();
                  }}
                  MenuProps={{
                    BackdropProps: {
                      sx: {
                        backgroundColor: 'transparent',
                        backdropFilter: 'none',
                      },
                    },
                  }}
                  displayEmpty
                  renderValue={() => {
                    return (
                      <Typography component='span'>
                        {getValues('category') ? (
                          <FormattedMessage
                            id={`contact_us.${categoryList.find(
                              category => category === getValues('category')
                            )}`}
                          />
                        ) : (
                          ''
                        )}
                      </Typography>
                    );
                  }}
                >
                  {categoryList.map((item, index) => {
                    return (
                      <MenuItem value={item} key={index} id={`report-muvi-${item}`}>
                        <Radio checked={item === getValues('category')} />
                        <FormattedMessage id={`contact_us.${item}`} />
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <FormControl>
                <TextField
                  disabled
                  id='report-muvi-subject'
                  label={
                    <>
                      <FormattedMessage id='contact_us.subject' />
                    </>
                  }
                  variant='outlined'
                  {...register('subject')}
                  error={(errors.subject && touchedFields.subject) ?? false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentComponent
                        errorCondition={errors.subject && touchedFields.subject}
                      />
                    ),
                  }}
                />
              </FormControl>
              <FormControl>
                <TextField
                  disabled
                  id='contact-us-description'
                  label={
                    <>
                      <FormattedMessage id='contact_us.description' />
                    </>
                  }
                  variant='outlined'
                  {...register('description')}
                  error={(errors.description && touchedFields.description) ?? false}
                  InputProps={{
                    endAdornment: (
                      <InputAdornmentComponent
                        errorCondition={errors.description && touchedFields.description}
                      />
                    ),
                  }}
                  multiline
                />
              </FormControl>
              {!user?.phoneNumber && (
                <FormControl sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={publicRuntimeConfig.recaptchaClientKey}
                    onChange={onChangeCaptcha}
                    hl={locale}
                  />
                </FormControl>
              )}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          id={'report-muvi-submit'}
          variant='contained'
          loading={createContactUs.isLoading}
          sx={{
            width: '100%',
            height: { tablet: pxToRem(56), mobile: '56px' },
            borderRadius: '16px',
          }}
          onClick={() => onSubmit()}
          disabled={
            !isValid || (!Boolean(recaptchaValue) && !Boolean(user?.phoneNumber) && !user)
          }
        >
          <Typography
            component='p'
            sx={{
              fontSize: pxToRem(16),
              fontWeight: '700',
              lineHeight: '24px',
            }}
          >
            <FormattedMessage id='global.submit' />
          </Typography>
        </LoadingButton>
      </DialogActions>
    </>
  );
};
export default ReportMuviTeamDialog;
